'use strict';
/**
 * azure-functions/shared/risk/riskDefaults.cjs
 *
 * The one source of the default risk method (matrix, levels, thresholds, mode,
 * approver role) and of its effective form. Browser-safe: no Node imports, so
 * src/constants/riskConfig.js re-exports it and risk-method-write compares with
 * it (GH #8371).
 */

// Build a set of levels for a given matrix dimension n (e.g. n=3 → levels 1,2,3)
function buildDefaultLevels(n) {
  const colorRamps = {
    2: ['#4ade80', '#f43f5e'],
    3: ['#4ade80', '#facc15', '#f43f5e'],
    4: ['#4ade80', '#86efac', '#fb923c', '#f43f5e'],
    5: ['#22d3ee', '#4ade80', '#facc15', '#fb923c', '#f43f5e'],
    6: ['#22d3ee', '#4ade80', '#86efac', '#facc15', '#fb923c', '#f43f5e'],
    7: ['#22d3ee', '#4ade80', '#86efac', '#fde68a', '#facc15', '#fb923c', '#f43f5e'],
    8: ['#22d3ee', '#67e8f9', '#4ade80', '#86efac', '#facc15', '#fb923c', '#f87171', '#f43f5e'],
    9: [
      '#22d3ee',
      '#67e8f9',
      '#4ade80',
      '#86efac',
      '#fde68a',
      '#facc15',
      '#fb923c',
      '#f87171',
      '#f43f5e',
    ],
    10: [
      '#22d3ee',
      '#67e8f9',
      '#38bdf8',
      '#4ade80',
      '#86efac',
      '#fde68a',
      '#facc15',
      '#fb923c',
      '#f87171',
      '#f43f5e',
    ],
  };

  const impactLabels = {
    2: ['Low', 'High'],
    3: ['Low', 'Medium', 'High'],
    4: ['Negligible', 'Low', 'High', 'Critical'],
    5: ['Negligible', 'Low', 'Medium', 'High', 'Critical'],
    6: ['Negligible', 'Very Low', 'Low', 'Medium', 'High', 'Critical'],
    7: ['Negligible', 'Very Low', 'Low', 'Medium', 'High', 'Very High', 'Critical'],
    8: ['Negligible', 'Very Low', 'Low', 'Moderate', 'Medium', 'High', 'Very High', 'Critical'],
    9: [
      'Negligible',
      'Very Low',
      'Low',
      'Moderate',
      'Medium',
      'Elevated',
      'High',
      'Very High',
      'Critical',
    ],
    10: [
      'Negligible',
      'Very Low',
      'Low',
      'Moderate',
      'Medium',
      'Elevated',
      'High',
      'Very High',
      'Severe',
      'Critical',
    ],
  };

  const likelihoodLabels = {
    2: ['Unlikely', 'Likely'],
    3: ['Rare', 'Possible', 'Likely'],
    4: ['Rare', 'Unlikely', 'Likely', 'Almost Certain'],
    5: ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'],
    6: ['Rare', 'Very Unlikely', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'],
    7: ['Rare', 'Very Unlikely', 'Unlikely', 'Possible', 'Likely', 'Very Likely', 'Almost Certain'],
    8: [
      'Rare',
      'Very Unlikely',
      'Unlikely',
      'Occasional',
      'Possible',
      'Likely',
      'Very Likely',
      'Almost Certain',
    ],
    9: [
      'Rare',
      'Very Unlikely',
      'Unlikely',
      'Occasional',
      'Possible',
      'Probable',
      'Likely',
      'Very Likely',
      'Almost Certain',
    ],
    10: [
      'Rare',
      'Very Unlikely',
      'Unlikely',
      'Occasional',
      'Possible',
      'Probable',
      'Likely',
      'Very Likely',
      'Near Certain',
      'Almost Certain',
    ],
  };

  const impactDescs = {
    2: [
      'Minimal financial loss or operational disruption. Easily recoverable.',
      'Major financial loss, reputational damage, or significant regulatory breach.',
    ],
    3: [
      'Minimal financial loss or operational disruption. Easily recoverable within hours.',
      'Moderate financial loss or reputational damage. Recovery within days.',
      'Major financial loss, regulatory breach, or significant reputational harm. Long recovery.',
    ],
    4: [
      'Negligible impact. No measurable operational or financial consequence.',
      'Low impact. Minor disruption, quickly contained and resolved.',
      'High impact. Significant financial or reputational damage requiring substantial remediation.',
      'Critical impact. Existential threat, legal liability, or major regulatory sanction.',
    ],
    5: [
      'Negligible. No meaningful consequence.',
      'Low. Minor disruption, quickly resolved.',
      'Medium. Moderate financial or reputational effect.',
      'High. Significant harm requiring major remediation effort.',
      'Critical. Existential or catastrophic consequence.',
    ],
  };

  const likelihoodDescs = {
    2: [
      'Unlikely to occur within the foreseeable future. No known history of occurrence.',
      'Likely to occur — perhaps multiple times per year.',
    ],
    3: [
      'Rare — may happen once in a decade or less. No known precedent.',
      'Possible — could happen sometime during the year.',
      'Likely — expected to occur multiple times per year.',
    ],
    4: [
      'Rare — may happen once in a decade or more.',
      'Unlikely — may happen once every few years.',
      'Likely — expected to happen at least once a year.',
      'Almost certain — expected to happen frequently, multiple times a year.',
    ],
    5: [
      'Rare — once in a decade or less.',
      'Unlikely — once every 2–5 years.',
      'Possible — once a year or so.',
      'Likely — several times a year.',
      'Almost certain — monthly or more frequent.',
    ],
  };

  const colors = colorRamps[n] || colorRamps[5];
  const iLabels = impactLabels[n] || Array.from({ length: n }, (_, i) => `Level ${i + 1}`);
  const lLabels = likelihoodLabels[n] || Array.from({ length: n }, (_, i) => `Level ${i + 1}`);
  const iDescs = impactDescs[n] || Array(n).fill('');
  const lDescs = likelihoodDescs[n] || Array(n).fill('');

  return {
    impactLevels: Array.from({ length: n }, (_, i) => ({
      value: i + 1,
      label: iLabels[i],
      color: colors[i],
      description: iDescs[i] || '',
    })),
    likelihoodLevels: Array.from({ length: n }, (_, i) => ({
      value: i + 1,
      label: lLabels[i],
      color: colors[i],
      description: lDescs[i] || '',
    })),
  };
}

function buildDefaultThresholds(n) {
  const maxScore = n * n;
  if (n === 2)
    return [
      { max: 1, label: 'Low', color: '#4ade80' },
      { max: 2, label: 'Medium', color: '#facc15' },
      { max: maxScore, label: 'High', color: '#f43f5e' },
    ];
  if (n <= 4)
    return [
      { max: Math.ceil(maxScore * 0.2), label: 'Low', color: '#4ade80' },
      { max: Math.ceil(maxScore * 0.45), label: 'Medium', color: '#facc15' },
      { max: Math.ceil(maxScore * 0.7), label: 'High', color: '#fb923c' },
      { max: maxScore, label: 'Critical', color: '#f43f5e' },
    ];
  return [
    { max: Math.ceil(maxScore * 0.16), label: 'Low', color: '#4ade80' },
    { max: Math.ceil(maxScore * 0.36), label: 'Medium', color: '#facc15' },
    { max: Math.ceil(maxScore * 0.64), label: 'High', color: '#fb923c' },
    { max: maxScore, label: 'Critical', color: '#f43f5e' },
  ];
}

const SUPPORTED_MATRIX_SIZES = [2, 3, 4, 5, 6, 7, 8, 9, 10];

function buildDefaultSettings(n = 5) {
  const { impactLevels, likelihoodLevels } = buildDefaultLevels(n);
  return {
    matrixSize: n,
    // Tenant-level Risk Mode UX. 'advanced' = full governance UI (current
    // behaviour, back-compat default so no existing tenant is downgraded);
    // 'basic' = simplified projection. Gated by the advancedRiskMode plan
    // feature — see riskModeProjection.js and the state-write data-layer gate.
    mode: 'advanced',
    // The approver is auto-assigned to the first user with this role. Absent or null
    // is read as 'admin' by every consumer (governance.resolveDefaultApprover).
    fixedApproverRole: 'admin',
    impactLevels,
    likelihoodLevels,
    scoreThresholds: buildDefaultThresholds(n),
  };
}

const DEFAULT_RISK_SETTINGS = buildDefaultSettings(5);

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * The method every consumer actually reads: stored riskSettings completed with
 * the defaults for its matrix size. Absent mode is advanced; absent or null
 * fixedApproverRole is 'admin' (governance.resolveDefaultApprover,
 * normalizeRiskMode). A stored value is never rewritten — this is the
 * comparison form only.
 */
function effectiveRiskSettings(stored) {
  const v = isObj(stored) ? stored : {};
  const size = Number.isInteger(v.matrixSize) && v.matrixSize >= 2 && v.matrixSize <= 10 ? v.matrixSize : 5;
  const out = { ...buildDefaultSettings(size) };
  for (const k of Object.keys(v)) if (v[k] !== undefined && v[k] !== null) out[k] = v[k];
  out.mode = out.mode === 'basic' ? 'basic' : 'advanced';
  out.fixedApproverRole = out.fixedApproverRole || 'admin';
  return out;
}

module.exports = {
  buildDefaultLevels,
  buildDefaultThresholds,
  SUPPORTED_MATRIX_SIZES,
  buildDefaultSettings,
  DEFAULT_RISK_SETTINGS,
  effectiveRiskSettings,
};
